import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

const tooltipContentVariants = cva(
  'animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 z-50 max-w-sm rounded-md px-3 py-1.5 text-xs',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground ',
        destructive: 'bg-destructive text-destructive-foreground',
        warning: 'bg-warning text-warning-foreground bg-white border border-warning',
        info: 'bg-white text-blue-500 border border-blue-500',
        success: 'bg-success text-success-foreground bg-white border border-success',
        error: 'bg-error text-error-foreground bg-white border border-error',
        outline: 'border border-input bg-white shadow-xs',
      },
      defaultVariants: {
        variant: 'default',
      },
    },
  },
);

function TooltipProvider({ delayDuration = 0, ...props }: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return <TooltipPrimitive.Provider data-slot="tooltip-provider" delayDuration={delayDuration} {...props} />;
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

function TooltipContent({
  className,
  sideOffset = 4,
  children,
  variant = 'default',
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content> & VariantProps<typeof tooltipContentVariants>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(tooltipContentVariants({ variant, className }))}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow
          className={cn(
            'z-50 size-2.5 translate-y-[calc(-50%_-_2px)] rotate-45 rounded-[2px]',
            variant === 'default'
              ? 'bg-primary fill-primary'
              : variant === 'destructive'
                ? 'bg-destructive fill-destructive'
                : variant === 'warning'
                  ? 'bg-warning fill-warning'
                  : variant === 'info'
                    ? 'bg-white text-blue-500 border-blue-500'
                    : variant === 'success'
                      ? 'bg-success fill-success'
                      : variant === 'error'
                        ? 'bg-error fill-error'
                        : variant === 'outline'
                          ? 'bg-white border border-input'
                          : '',
          )}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
